import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Chip,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    InputAdornment,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Language as LanguageIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Language {
    code: string;
    name: string;
    native_name: string;
    is_rtl: boolean;
    is_active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

interface LanguageFormData {
    code: string;
    name: string;
    native_name: string;
    is_rtl: boolean;
    is_active: boolean;
    display_order: number;
}

interface FormErrors {
    code?: string;
    name?: string;
    native_name?: string;
    display_order?: string;
}

const Languages: React.FC = () => {
    const [languages, setLanguages] = useState<Language[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
    const [formData, setFormData] = useState<LanguageFormData>({
        code: '',
        name: '',
        native_name: '',
        is_rtl: false,
        is_active: true,
        display_order: 0,
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);

    const fetchLanguages = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: (page + 1).toString(),
                page_size: rowsPerPage.toString(),
            });

            if (searchTerm) {
                params.append('search', searchTerm);
            }

            if (statusFilter !== 'all') {
                params.append('is_active', statusFilter === 'active' ? 'true' : 'false');
            }

            const response = await api.get(`/super-admin/languages/?${params}`);
            console.log('Languages response:', response.data);

            setLanguages(response.data.results || response.data);
            setTotalCount(response.data.count || response.data.length);
        } catch (error: any) {
            console.error('Error fetching languages:', error);
            setError(
                error.response?.data?.error ||
                error.message ||
                'Failed to fetch languages'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLanguages();
    }, [page, rowsPerPage, searchTerm, statusFilter]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleStatusFilterChange = (event: any) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const handleAddLanguage = () => {
        setEditingLanguage(null);
        setFormData({
            code: '',
            name: '',
            native_name: '',
            is_rtl: false,
            is_active: true,
            display_order: 0,
        });
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleEditLanguage = (language: Language) => {
        setEditingLanguage(language);
        setFormData({
            code: language.code,
            name: language.name,
            native_name: language.native_name,
            is_rtl: language.is_rtl,
            is_active: language.is_active,
            display_order: language.display_order,
        });
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleDeleteLanguage = async (code: string) => {
        if (!window.confirm('Are you sure you want to delete this language? This action cannot be undone.')) {
            return;
        }

        try {
            await api.delete(`/super-admin/languages/${code}/`);
            fetchLanguages();
        } catch (error: any) {
            console.error('Error deleting language:', error);
            alert('Failed to delete language: ' + (error.response?.data?.error || error.message));
        }
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {};

        if (!formData.code?.trim()) {
            errors.code = 'Language code is required';
        } else if (!/^[a-z]{2,3}(-[A-Z]{2})?$/.test(formData.code)) {
            errors.code = 'Invalid language code format (e.g., en, en-US)';
        }

        if (!formData.name?.trim()) {
            errors.name = 'Language name is required';
        }

        if (!formData.native_name?.trim()) {
            errors.native_name = 'Native name is required';
        }

        if (formData.display_order < 0) {
            errors.display_order = 'Display order must be a positive number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            if (editingLanguage) {
                await api.put(`/super-admin/languages/${editingLanguage.code}/`, formData);
            } else {
                await api.post('/super-admin/languages/', formData);
            }

            setDialogOpen(false);
            fetchLanguages();
        } catch (error: any) {
            console.error('Error saving language:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to save language';

            // Handle field-specific errors
            if (error.response?.data) {
                const fieldErrors: FormErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    if (key in formData) {
                        fieldErrors[key as keyof FormErrors] = Array.isArray(error.response.data[key])
                            ? error.response.data[key][0]
                            : error.response.data[key];
                    }
                });
                setFormErrors(fieldErrors);
            } else {
                alert(errorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleFormChange = (field: keyof LanguageFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field in formErrors) {
            setFormErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    if (loading && languages.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                        Languages Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage all languages in the system
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddLanguage}
                >
                    Add Language
                </Button>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                            <TextField
                                fullWidth
                                placeholder="Search languages..."
                                value={searchTerm}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    label="Status"
                                >
                                    <MenuItem value="all">All Statuses</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchLanguages} color="primary">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Languages Table */}
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Native Name</TableCell>
                                <TableCell>RTL</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Display Order</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {languages.map((language) => (
                                <TableRow key={language.code} hover>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <LanguageIcon fontSize="small" color="action" />
                                            <Typography variant="body2" fontWeight="medium">
                                                {language.code}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {language.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {language.native_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {language.is_rtl ? (
                                            <CheckCircleIcon fontSize="small" color="success" />
                                        ) : (
                                            <CancelIcon fontSize="small" color="disabled" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={language.is_active ? 'Active' : 'Inactive'}
                                            color={language.is_active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {language.display_order}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {new Date(language.created_at).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditLanguage(language)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteLanguage(language.code)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingLanguage ? 'Edit Language' : 'Add New Language'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Language Code"
                            value={formData.code}
                            onChange={(e) => handleFormChange('code', e.target.value)}
                            error={!!formErrors.code}
                            helperText={formErrors.code || 'e.g., en, es, fr, en-US'}
                            required
                            disabled={!!editingLanguage}
                        />
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => handleFormChange('name', e.target.value)}
                            error={!!formErrors.name}
                            helperText={formErrors.name || 'e.g., English, Spanish, French'}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Native Name"
                            value={formData.native_name}
                            onChange={(e) => handleFormChange('native_name', e.target.value)}
                            error={!!formErrors.native_name}
                            helperText={formErrors.native_name || 'e.g., English, Español, Français'}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Display Order"
                            type="number"
                            value={formData.display_order}
                            onChange={(e) => handleFormChange('display_order', parseInt(e.target.value) || 0)}
                            error={!!formErrors.display_order}
                            helperText={formErrors.display_order || 'Order in which language appears in lists'}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_rtl}
                                    onChange={(e) => handleFormChange('is_rtl', e.target.checked)}
                                />
                            }
                            label="Right-to-Left (RTL)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? 'Saving...' : editingLanguage ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Languages;
